import styled from "styled-components";

import type { ActivityEvent, ActivityKind, ActorType } from "../api/types";
import { theme } from "../theme";
import { relativeTime } from "../lib/format";

// Home "// chatter": player actions, Rocky lines, and bot/system events in one
// feed (PRD decision #7). `summary` and `deltaLabel` are server-provided text.
const dotColor: Record<ActorType, string> = {
  user: theme.color.accent,
  rocky: theme.color.satiety,
  system: theme.color.textDim,
};

// Tint the `deltaLabel` by what happened, matching the design's colored deltas.
const deltaColor: Record<ActivityKind, string> = {
  feed: theme.color.satiety,
  clean: theme.color.hygiene,
  riddle_solved: theme.color.happiness,
  quest: theme.color.accent,
  lore: theme.color.accent,
  decay: theme.color.danger,
  decay_warning: theme.color.warn,
};

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(3)};
`;

const Item = styled.li`
  display: grid;
  grid-template-columns: 8px 1fr auto;
  gap: ${({ theme }) => theme.space(3)};
  align-items: start;
`;

const Dot = styled.span<{ $actor: ActorType }>`
  width: 8px;
  height: 8px;
  margin-top: 6px;
  border-radius: 50%;
  background: ${({ $actor }) => dotColor[$actor]};
`;

const Summary = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.color.text};
`;

const Delta = styled.span<{ $color: string }>`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  color: ${({ $color }) => $color};
  margin-left: 6px;
`;

const Time = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.color.textFaint};
  white-space: nowrap;
`;

const Empty = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 13px;
  color: ${({ theme }) => theme.color.textFaint};
`;

export function ActivityFeed({ events }: { events: ActivityEvent[] }) {
  if (events.length === 0) {
    return <Empty>quiet in the den…</Empty>;
  }
  return (
    <List>
      {events.map((e) => (
        <Item key={e.id}>
          <Dot $actor={e.actorType} />
          <Summary>
            {e.summary}
            {e.deltaLabel && <Delta $color={deltaColor[e.kind]}>{e.deltaLabel}</Delta>}
          </Summary>
          <Time>{relativeTime(e.createdAt)}</Time>
        </Item>
      ))}
    </List>
  );
}
